// Admin page: audit log of all incoming SePay webhook events.
// Route: /admin/webhooks  (admin + manager)
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { X, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface WebhookEvent {
  id: string;
  provider: string;
  eventId: string;
  payload: unknown;
  signature?: string | null;
  verified: boolean;
  matchedOrderId?: string | null;
  matchedTransactionId?: string | null;
  processedAt?: string | null;
  error?: string | null;
  receivedAt: string;
}

interface ListResponse {
  data: WebhookEvent[];
  meta: { total: number; page: number; limit: number };
}

function fmtDate(s: string | null | undefined) {
  if (!s) return "-";
  return new Date(s).toLocaleString("vi-VN", { hour12: false });
}

// Modal: shows raw payload JSON.
function PayloadModal({ event, onClose }: { event: WebhookEvent; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl rounded-xl border bg-card p-6 shadow-xl flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-base">Webhook payload — {event.eventId}</h3>
          <Button variant="ghost" size="icon" onClick={onClose}><X size={18} /></Button>
        </div>
        {event.error && (
          <div className="mb-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            Lỗi: {event.error}
          </div>
        )}
        <pre className="flex-1 overflow-auto rounded-md bg-muted p-4 text-xs font-mono whitespace-pre-wrap">
          {JSON.stringify(event.payload, null, 2)}
        </pre>
      </div>
    </div>
  );
}

export function WebhookLogPage() {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<WebhookEvent | null>(null);
  const limit = 20;

  // Use apiClient directly to get the full {data, meta} envelope from OKWithMeta.
  const { data: fullData, isLoading, isError } = useQuery({
    queryKey: ["webhook-events", page],
    queryFn: async () => {
      const res = await apiClient.get(`/v1/webhook-events?page=${page}&limit=${limit}`);
      return res.data as ListResponse;
    },
    staleTime: 10_000,
  });

  const events = fullData?.data ?? [];
  const total = fullData?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  if (isLoading) return <div className="p-6 text-muted-foreground">Đang tải...</div>;
  if (isError) return <div className="p-6 text-destructive">Lỗi tải dữ liệu webhook.</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Webhook Log</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Lịch sử nhận webhook từ SePay — {total} bản ghi
          </p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Thời gian nhận</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Event ID</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Provider</th>
              <th className="px-4 py-3 text-center font-medium text-muted-foreground">Xác thực</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Đơn hàng</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Xử lý lúc</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Lỗi</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {events.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  Chưa có webhook nào.
                </td>
              </tr>
            )}
            {events.map((ev) => (
              <tr
                key={ev.id}
                className="hover:bg-muted/30 cursor-pointer transition-colors"
                onClick={() => setSelected(ev)}
              >
                <td className="px-4 py-3 font-mono text-xs">{fmtDate(ev.receivedAt)}</td>
                <td className="px-4 py-3 font-mono text-xs max-w-[140px] truncate">{ev.eventId}</td>
                <td className="px-4 py-3">{ev.provider}</td>
                <td className="px-4 py-3 text-center">
                  {ev.verified
                    ? <CheckCircle size={16} className="text-green-500 mx-auto" />
                    : <XCircle size={16} className="text-destructive mx-auto" />}
                </td>
                <td className="px-4 py-3 font-mono text-xs max-w-[120px] truncate">
                  {ev.matchedOrderId ?? <span className="text-muted-foreground">-</span>}
                </td>
                <td className="px-4 py-3 text-xs">{fmtDate(ev.processedAt)}</td>
                <td className="px-4 py-3 text-xs text-destructive max-w-[180px] truncate">
                  {ev.error ?? ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Trang {page} / {totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Trước
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Tiếp
            </Button>
          </div>
        </div>
      )}

      {/* Payload modal */}
      {selected && <PayloadModal event={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
